import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sparkles, Loader2, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import {
  geminiIntegrationService,
} from '@/features/gemini-integration/services/gemini-integration.service';
import { useToast } from '@/hooks/use-toast';

export const Route = createFileRoute('/_authenticated/gemini-config')({
  component: GeminiConfigPage,
});

function GeminiConfigPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [model, setModel] = useState<string | null>(null);

  const { data: config, isLoading } = useQuery({
    queryKey: ['gemini-integration-config'],
    queryFn: () => geminiIntegrationService.getConfig(),
  });

  // Só busca a lista depois que já se sabe se há chave (própria ou padrão) disponível.
  const { data: models, isLoading: loadingModels, isError: modelsError } = useQuery({
    queryKey: ['gemini-integration-models'],
    queryFn: () => geminiIntegrationService.listModels(),
    enabled: !!config?.available,
    staleTime: 5 * 60 * 1000,
  });

  const updateMutation = useMutation({
    mutationFn: (data: { apiKey?: string; model?: string }) =>
      geminiIntegrationService.updateConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gemini-integration-config'] });
      toast({ title: 'Configuração salva', description: 'A integração com o Gemini foi atualizada.' });
      setApiKey('');
    },
    onError: (err: any) => {
      toast({
        title: err?.response?.data?.message ?? 'Erro ao salvar',
        variant: 'destructive',
      });
    },
  });

  const handleSaveApiKey = () => {
    updateMutation.mutate({ apiKey });
  };

  const handleClearApiKey = () => {
    setApiKey('');
    updateMutation.mutate({ apiKey: '' });
  };

  const handleSaveModel = () => {
    updateMutation.mutate({ model: model ?? '' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const currentModel = model ?? config?.model ?? '';

  return (
    <PermissionGate permission={Permission.AI_ANALYSIS_MANAGE}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Gemini (IA)</h1>
          <p className="text-muted-foreground mt-2">
            Configure a chave de API do Google Gemini usada pelas funcionalidades de IA (análise de boards, "Novo lead com IA", etc).
            Se nenhuma chave própria for configurada, o sistema usa a chave padrão do servidor.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Status da integração
            </CardTitle>
            <CardDescription>
              Situação atual da integração com a API do Gemini para este tenant.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Disponibilidade:</span>
              {config?.available ? (
                <Badge className="gap-1 bg-green-600 hover:bg-green-600">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Disponível
                </Badge>
              ) : (
                <Badge variant="destructive" className="gap-1">
                  <XCircle className="h-3.5 w-3.5" />
                  Indisponível
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Chave própria configurada:</span>
              <Badge variant={config?.hasCustomApiKey ? 'default' : 'secondary'}>
                {config?.hasCustomApiKey ? 'Sim' : 'Não (usando chave padrão do sistema)'}
              </Badge>
            </div>
            {config?.hasCustomApiKey && config?.apiKeyMasked && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Chave atual:</span>
                <code className="text-sm bg-muted px-2 py-0.5 rounded">{config.apiKeyMasked}</code>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Modelo em uso:</span>
              <code className="text-sm bg-muted px-2 py-0.5 rounded">{config?.effectiveModel}</code>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Chave de API (Google AI Studio)</CardTitle>
            <CardDescription>
              Gere uma chave em{' '}
              <span className="font-medium">aistudio.google.com/app/apikey</span> e cole abaixo.
              Deixe em branco e clique em "Remover chave" para voltar a usar a chave padrão do sistema.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2 max-w-md">
              <Label htmlFor="gemini-api-key">Chave de API</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="gemini-api-key"
                    type={showApiKey ? 'text' : 'password'}
                    placeholder={config?.hasCustomApiKey ? config?.apiKeyMasked ?? '' : 'AIza...'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveApiKey} disabled={updateMutation.isPending || !apiKey.trim()}>
                {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Salvar chave
              </Button>
              {config?.hasCustomApiKey && (
                <Button variant="outline" onClick={handleClearApiKey} disabled={updateMutation.isPending}>
                  Remover chave
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Modelo</CardTitle>
            <CardDescription>
              Opcional. Escolha um modelo específico do Gemini para esta conta.
              Deixe em branco para usar o padrão do sistema.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2 max-w-md">
              <Label htmlFor="gemini-model">Modelo</Label>
              {modelsError ? (
                // Fallback: sem lista (ex: chave inválida), permite digitar manualmente.
                <Input
                  id="gemini-model"
                  placeholder="gemini-2.0-flash"
                  value={currentModel}
                  onChange={(e) => setModel(e.target.value)}
                />
              ) : (
                <Select
                  value={currentModel || '__default__'}
                  onValueChange={(v) => setModel(v === '__default__' ? '' : v)}
                  disabled={loadingModels}
                >
                  <SelectTrigger id="gemini-model">
                    <SelectValue
                      placeholder={loadingModels ? 'Carregando modelos...' : 'Padrão do sistema'}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">Padrão do sistema</SelectItem>
                    {models?.map((m) => (
                      <SelectItem key={m.name} value={m.name}>
                        {m.displayName} ({m.name})
                      </SelectItem>
                    ))}
                    {/* Modelo salvo mas fora da lista atual (ex: preview descontinuado) — mantém selecionável. */}
                    {currentModel && !models?.some((m) => m.name === currentModel) && (
                      <SelectItem value={currentModel}>{currentModel}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
            <Button onClick={handleSaveModel} disabled={updateMutation.isPending}>
              {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salvar modelo
            </Button>
          </CardContent>
        </Card>
      </div>
    </PermissionGate>
  );
}
